'use client'

import Link from 'next/link'
import { useState, useEffect } from 'react'
import { useAuth, SignedIn, SignedOut } from '@/lib/auth/context'
import { isAdminUserClient } from '@/lib/admin/allowlist'
import { getDefaultAppHome } from '@/lib/admin/routes'
import { useAuthModal } from '@/components/auth/auth-modal-context'
import { Button } from '@/components/ui/button'
import { Logo } from './logo'
import { Menu, X, LogOut, ChevronDown } from 'lucide-react'
import { NavLink, PremiumLink } from '@/components/analytics/tracked-link'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/**
 * Navegação pública. O Comprovante fica aqui de propósito: é a ferramenta que
 * mais converte e, até jul/2026, não estava em nenhum menu do site.
 */
const publicNavLinks = [
  { href: '/calculadoras', label: 'Calculadoras' },
  { href: '/comprovante-de-faturamento-mei', label: 'Comprovante' },
  { href: '/blog', label: 'Blog' },
  { href: '/atividades-mei', label: 'Profissões MEI' },
]

/** Conta grátis: mesma superfície pública (painel é Premium). */
const freeNavLinks = publicNavLinks

/** Premium: funções trabalhadas (painel, orçamento, comprovante). */
const premiumNavLinks = [
  { href: '/dashboard', label: 'Painel' },
  { href: '/dashboard/planejamento', label: 'Orçamento' },
  { href: '/registrar', label: 'Registrar' },
  { href: '/comprovante-de-faturamento-mei', label: 'Comprovante' },
  { href: '/calculadoras', label: 'Calculadoras' },
  { href: '/blog', label: 'Blog' },
]

const adminNavLinks = [
  { href: '/admin', label: 'Command center' },
  { href: '/admin/campanhas', label: 'Campanhas' },
  { href: '/admin/blog', label: 'Blog' },
]

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const { user, isSignedIn, signOut } = useAuth()
  const { openLogin, openSignup } = useAuthModal()

  // Trava scroll do body quando overlay mobile aberto
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  const isAdmin = isAdminUserClient(user)
  const isPremium = user?.plano === 'PREMIUM' || user?.role === 'ADMIN'
  const homeHref = isSignedIn ? (isPremium || isAdmin ? getDefaultAppHome(user) : '/calculadoras') : '/'
  const signedInLinks = isAdmin
    ? adminNavLinks
    : isPremium
      ? premiumNavLinks
      : freeNavLinks

  return (
    <>
      <header
        className="sticky top-0 z-40 w-full bg-card border-b-2 border-border shadow-soft"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div>
          <div className="container-app flex h-[64px] sm:h-[68px] items-center justify-between">
            {/* Logo */}
            <Link
              href={homeHref}
              className="flex items-center"
              aria-label="Calcula MEI — início"
            >
              <Logo />
            </Link>

            {/* Desktop nav */}
            <nav
              className="hidden md:flex items-center gap-1"
              aria-label="Navegação principal"
            >
                <SignedOut>
                  {publicNavLinks.map((link) => (
                    <NavLink
                      key={link.href}
                      href={link.href}
                      secao="header"
                      rotulo={link.label}
                      className="px-3 py-2 rounded-lg text-[15px] font-semibold text-foreground/80 hover:text-foreground hover:bg-secondary transition-colors"
                    >
                      {link.label}
                    </NavLink>
                  ))}
                </SignedOut>

                <SignedIn>
                  {signedInLinks.map((link) => (
                    <NavLink
                      key={link.href}
                      href={link.href}
                      secao="header"
                      rotulo={link.label}
                      className="px-3 py-2 rounded-lg text-[15px] font-semibold text-foreground/80 hover:text-foreground hover:bg-secondary transition-colors"
                    >
                      {link.label}
                    </NavLink>
                  ))}
              </SignedIn>
            </nav>

            {/* Auth direita */}
            <div className="hidden md:flex items-center gap-3">
              <SignedOut>
                <PremiumLink
                  href="/premium"
                  local="header"
                  className="px-3 py-2 rounded-lg text-[15px] font-semibold text-foreground/80 hover:text-foreground hover:bg-secondary transition-colors"
                >
                  Premium
                </PremiumLink>
                <Button variant="ghost" size="sm" onClick={() => openLogin('header')} className="font-semibold">
                  Entrar
                </Button>
                <Button variant="secondary" size="sm" onClick={() => openSignup('header')} className="font-bold">
                  Criar conta
                </Button>
              </SignedOut>

              <SignedIn>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex h-10 items-center gap-2.5 rounded-lg border border-border pl-1.5 pr-2.5 hover:border-[rgb(var(--ink))] transition-colors"
                      aria-label="Menu da conta"
                    >
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--green-soft))] text-[13px] font-bold text-[rgb(var(--green-deep))]">
                        {(user?.name?.[0] ?? 'C').toUpperCase()}
                      </span>
                      <span className="flex flex-col items-start leading-tight">
                        <span className="max-w-[130px] truncate text-[13px] font-bold text-foreground">
                          {user?.name?.split(' ')[0] ?? 'Conta'}
                        </span>
                        <span className="text-[11px] font-medium text-foreground/55">
                          {isAdmin ? 'Admin' : user?.plano === 'PREMIUM' ? 'Premium' : 'Plano grátis'}
                        </span>
                      </span>
                      <ChevronDown className="h-4 w-4 shrink-0 text-foreground/45" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-2">
                      <p className="text-sm font-medium truncate">{user?.name || 'Usuário'}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      {isAdmin ? (
                        <Link href="/admin" className="cursor-pointer">
                          Command center
                        </Link>
                      ) : (
                        <Link href="/dashboard/configuracoes" className="cursor-pointer">
                          Configurações
                        </Link>
                      )}
                    </DropdownMenuItem>
                    {!isAdmin && (
                      <DropdownMenuItem asChild>
                        <PremiumLink href="/premium" local="header" className="cursor-pointer">
                          Premium
                        </PremiumLink>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={signOut}
                      className="cursor-pointer text-[rgb(var(--rose))]"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SignedIn>
            </div>

            {/* Mobile trigger */}
            <div className="md:hidden flex items-center gap-1">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-secondary transition-colors"
                aria-label="Abrir menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile overlay editorial — full-screen com tipografia grande */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 bg-background animate-fade-in md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Menu de navegação"
        >
          <div className="flex h-[68px] items-center justify-between border-b-2 border-border px-4 sm:px-6 bg-card">
            <Logo />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-card hover:bg-secondary transition-colors"
                aria-label="Fechar menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex flex-col h-[calc(100vh-68px)] overflow-y-auto bg-background">
            <nav className="flex flex-col px-6 py-8 gap-1" aria-label="Navegação mobile">
              <SignedOut>
                {publicNavLinks.map((link) => (
                  <NavLink
                    key={link.href}
                    href={link.href}
                    secao="header"
                    rotulo={link.label}
                    onClick={() => setMobileOpen(false)}
                    className="font-display text-2xl font-extrabold tracking-tight py-3.5 px-2 rounded-lg hover:bg-secondary hover:text-[rgb(var(--green-deep))] transition-colors"
                  >
                    {link.label}
                  </NavLink>
                ))}
              </SignedOut>

              <SignedIn>
                {signedInLinks.map((link) => (
                  <NavLink
                    key={link.href}
                    href={link.href}
                    secao="header"
                    rotulo={link.label}
                    onClick={() => setMobileOpen(false)}
                    className="font-display text-2xl font-extrabold tracking-tight py-3.5 px-2 rounded-lg hover:bg-secondary hover:text-[rgb(var(--green-deep))] transition-colors"
                  >
                    {link.label}
                  </NavLink>
                ))}
              </SignedIn>
            </nav>

            <div className="mt-auto border-t-2 border-border bg-card px-6 py-6 space-y-3">
              <SignedOut>
                <Button
                  variant="secondary"
                  className="w-full font-bold text-base h-12"
                  onClick={() => {
                    setMobileOpen(false)
                    openLogin('mobile_menu')
                  }}
                >
                  Entrar
                </Button>
                <Button
                  className="w-full font-bold text-base h-12"
                  onClick={() => {
                    setMobileOpen(false)
                    openSignup('mobile_menu')
                  }}
                >
                  Criar conta
                </Button>
                <PremiumLink
                  href="/premium"
                  local="header"
                  onClick={() => setMobileOpen(false)}
                  className="block text-center text-sm font-semibold text-foreground/70 hover:text-foreground pt-2"
                >
                  Premium
                </PremiumLink>
              </SignedOut>

              <SignedIn>
                <div className="pb-3 border-b border-border">
                  <p className="text-[15px] font-bold">{user?.name || 'Usuário'}</p>
                  <p className="text-sm text-foreground/70 font-medium">{user?.email}</p>
                </div>
                <Link
                  href={isAdmin ? '/admin' : '/dashboard/configuracoes'}
                  onClick={() => setMobileOpen(false)}
                  className="block text-[15px] font-semibold py-2.5 hover:text-[rgb(var(--green-deep))] transition-colors"
                >
                  {isAdmin ? 'Command center' : 'Configurações'}
                </Link>
                {!isAdmin && (
                  <Link
                    href="/premium"
                    onClick={() => setMobileOpen(false)}
                    className="block text-[15px] font-semibold py-2.5 hover:text-[rgb(var(--green-deep))] transition-colors"
                  >
                    Premium
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false)
                    signOut()
                  }}
                  className="block w-full text-left text-[15px] font-semibold py-2.5 text-[rgb(var(--rose))] hover:opacity-80 transition-opacity"
                >
                  Sair
                </button>
              </SignedIn>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
